import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { SUPERFICIE, COR_CATEGORIA } from './estilo';

const ROTA = '/admin/snapshot';
const VENDORS = ['HUAWEI', 'NOKIA', 'CISCO', 'JUNIPER'];

/** Valor comum a todas as grafias, ou null se divergirem. */
function uniforme(vendors, campo) {
  if (!vendors.length) return null;
  const primeiro = vendors[0][campo];
  return vendors.every((v) => v[campo] === primeiro) ? primeiro : null;
}

/**
 * Edicao de um conceito inteiro.
 *
 * A unidade aqui e o conceito, nao a linha do banco. "Versao do sistema" sao
 * quatro registros, um por vendor, e editar de um em um e como o catalogo
 * funcionava: alguem corrige a grafia do Huawei, esquece o Cisco, e a mesma
 * pergunta passa a ter duas respostas dependendo do equipamento — sem nada na
 * tela indicando isso, porque as linhas nunca apareciam juntas.
 *
 * Campo vazio REMOVE a grafia daquele vendor; preenchido onde nao havia,
 * acrescenta. E tudo ou nada: uma grafia barrada no portao de seguranca impede
 * a gravacao inteira, senao o conceito ficaria com versoes diferentes entre
 * vendors, que e exatamente o que agrupar existe para evitar.
 */
export default function PoolComandoDialog({ pool, categorias, onFechar, onSalvo }) {
  const notify = useNotify();

  const [rotulo, setRotulo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [textos, setTextos] = useState({});
  const [timeout, setTimeoutS] = useState('');
  const [pesado, setPesado] = useState(false);
  const [ativo, setAtivo] = useState(true);
  const [tocado, setTocado] = useState({});
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    if (!pool) return;
    setRotulo(pool.label || '');
    setCategoria(pool.category || '');
    setTextos(Object.fromEntries(
      VENDORS.map((v) => [v, pool.vendors.find((x) => x.vendor === v)?.command || ''])
    ));
    setTimeoutS(uniforme(pool.vendors, 'timeout_s') ?? '');
    setPesado(uniforme(pool.vendors, 'is_heavy') ?? false);
    setAtivo(uniforme(pool.vendors, 'enabled') ?? true);
    setTocado({});
    setErros({});
    setConfirmando(false);
  }, [pool]);

  const original = useMemo(() => Object.fromEntries(
    (pool?.vendors || []).map((v) => [v.vendor, v])
  ), [pool]);

  if (!pool) return null;

  const restantes = VENDORS.filter((v) => (textos[v] || '').trim()).length;
  const removendo = VENDORS.filter((v) => original[v] && !(textos[v] || '').trim());
  const incluindo = VENDORS.filter((v) => !original[v] && (textos[v] || '').trim());

  const salvar = async () => {
    setSalvando(true);
    setErros({});
    try {
      const res = await api.put(`${ROTA}/command/pool`, {
        empresa: pool.empresa,
        key: pool.key,
        label: pool.editable_label ? rotulo.trim() : undefined,
        category: pool.editable_label ? categoria : undefined,
        commands: textos,
        // Campos divergentes entre as grafias so vao se o operador mexer neles.
        // Mandar o valor exibido sobrescreveria em silencio o que ele nunca viu.
        timeout_s: tocado.timeout ? Number(timeout) || null : undefined,
        is_heavy: tocado.pesado ? pesado : undefined,
        enabled: tocado.ativo ? ativo : undefined,
      });

      if (res.data?.errors?.length) {
        const porCampo = {};
        res.data.errors.forEach((e) => { porCampo[e.field] = e.msg; });
        setErros(porCampo);
        return;
      }

      const d = res.data.data;
      notify.setNotify({
        open: true,
        severity: 'success',
        message: `“${d.label}”: ${d.updated} mantida(s), ${d.created} incluída(s), ${d.removed} removida(s)`,
      });
      onSalvo?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao salvar o conceito' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    setSalvando(true);
    try {
      const res = await api.post(`${ROTA}/command/pool/delete`, {
        empresa: pool.empresa, key: pool.key,
      });
      if (res.data?.errors?.length) {
        notify.setNotify({ open: true, severity: 'error', message: res.data.errors[0].msg });
        return;
      }
      notify.setNotify({
        open: true, severity: 'success',
        message: `“${res.data.data.label}” removido (${res.data.data.removed} grafia(s))`,
      });
      onSalvo?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao excluir' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!pool} onClose={onFechar} maxWidth="md" fullWidth PaperProps={{ sx: SUPERFICIE }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Editar conceito</span>
          <Chip
            size="small" label={pool.category}
            sx={{
              height: 19, color: '#fff',
              bgcolor: COR_CATEGORIA[pool.category] || COR_CATEGORIA.OUTROS,
            }}
          />
        </Stack>
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
          As grafias de todos os equipamentos mudam juntas
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {confirmando ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              Remover <strong>{pool.label}</strong> apaga as {pool.vendors.length} grafia(s)
              de todos os equipamentos.
            </Alert>
            {pool.used_by_sets > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pool.used_by_sets} lista(s) usam este comando. Elas continuam
                existindo e passam a coletar <strong>uma coisa a menos</strong> — sem
                nada na tela da coleta indicando o que sumiu.
              </Alert>
            )}
            <Typography variant="body2" sx={{ color: '#475569' }}>
              Para tirar só um equipamento, feche isto e apague o texto daquele
              campo — o conceito continua existindo.
            </Typography>
          </>
        ) : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
              <TextField
                size="small" label="Conceito" sx={{ flex: 1 }}
                value={rotulo} onChange={(e) => setRotulo(e.target.value)}
                disabled={!pool.editable_label}
                error={!!erros.label}
                helperText={erros.label || (pool.editable_label
                  ? 'Como este comando aparece nas listas'
                  : `Vem do fact ${pool.fact_key} — é ele que define o que o diff compara`)}
              />
              <TextField
                select size="small" label="Categoria" sx={{ width: 200 }}
                value={categoria} onChange={(e) => setCategoria(e.target.value)}
                disabled={!pool.editable_label}
                error={!!erros.category} helperText={erros.category || ' '}
              >
                {categorias.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Stack>

            {!pool.editable_label && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Conceito <strong>curado</strong>: o rótulo e a categoria pertencem ao
                fact, que define a identidade comparada no diff. Mudar ali alteraria
                a semântica da comparação, não a aparência da tela.
              </Alert>
            )}

            <Divider sx={{ mb: 1.5 }} />

            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
              Grafia por equipamento
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
              Campo vazio remove a grafia daquele equipamento. Preencher onde não
              havia acrescenta.
            </Typography>

            <Stack spacing={1.5}>
              {VENDORS.map((v) => {
                const atual = original[v];
                return (
                  <Stack key={v} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ width: 96, pt: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{v}</Typography>
                      {atual && !atual.has_parser && (
                        <Tooltip title="Coleta o texto, mas não entra no diff estruturado.">
                          <Typography variant="caption" sx={{ color: '#d97706' }}>
                            sem parser
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField
                      size="small" fullWidth
                      placeholder={atual ? '' : `sem grafia para ${v}`}
                      value={textos[v] || ''}
                      onChange={(e) => {
                        setTextos((t) => ({ ...t, [v]: e.target.value }));
                        setErros((x) => ({ ...x, [v]: null }));
                      }}
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                      error={!!erros[v]}
                      helperText={erros[v] || ' '}
                    />
                  </Stack>
                );
              })}
            </Stack>

            {!!incluindo.length && !pool.editable_label && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {incluindo.join(', ')} entra sem parser: a saída é coletada e
                comparada como texto, mas fica de fora do diff estruturado até
                alguém escrever o parser.
              </Alert>
            )}

            {!!removendo.length && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {removendo.join(', ')} será removido do catálogo.
              </Alert>
            )}

            {!pool.editable_label && (
              <Alert severity="info" sx={{ mb: 1 }}>
                O parser espera a saída <strong>deste</strong> comando. Trocar por
                uma variante (um <code>detail</code>, um filtro) muda o formato e o
                parser passa a não reconhecer nada — sem erro, só sem resultado.
              </Alert>
            )}

            {erros.commands && <Alert severity="error" sx={{ mb: 1 }}>{erros.commands}</Alert>}

            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small" label="Timeout (s)" sx={{ width: 130 }} type="number"
                value={timeout}
                onChange={(e) => { setTimeoutS(e.target.value); setTocado((t) => ({ ...t, timeout: true })); }}
                helperText={uniforme(pool.vendors, 'timeout_s') === null ? 'varia entre grafias' : ' '}
              />
              <FormControlLabel
                control={(
                  <Switch size="small" checked={pesado}
                    onChange={(e) => { setPesado(e.target.checked); setTocado((t) => ({ ...t, pesado: true })); }} />
                )}
                label={<Typography variant="caption">saída pesada</Typography>}
              />
              <FormControlLabel
                control={(
                  <Switch size="small" checked={ativo}
                    onChange={(e) => { setAtivo(e.target.checked); setTocado((t) => ({ ...t, ativo: true })); }} />
                )}
                label={<Typography variant="caption">ativo</Typography>}
              />
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {confirmando ? (
          <>
            <Button onClick={() => setConfirmando(false)}>Voltar</Button>
            <Button color="error" variant="contained" onClick={excluir} disabled={salvando}>
              {salvando ? 'Removendo…' : 'Remover conceito'}
            </Button>
          </>
        ) : (
          <>
            <Button
              color="error" startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmando(true)}
            >
              Excluir
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onFechar}>Cancelar</Button>
            <Button
              variant="contained" onClick={salvar}
              disabled={salvando || !restantes || (pool.editable_label && !rotulo.trim())}
            >
              {salvando ? 'Salvando…' : `Salvar ${restantes} grafia(s)`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
